import { useState, useEffect } from 'preact/hooks';
import type { Connection } from '../../../types';
import { connectionManagementService } from '../../../services/connections/connection-management.service';
import { agentsService } from '../../../services/agents.service';
import { useScopeContext } from '../../../contexts/ScopeContext';
import { toast } from '../../../services/toast.service';

interface AgentConnectionsManagerProps {
  agentId: string;
}

export function AgentConnectionsManager({ agentId }: AgentConnectionsManagerProps) {
  const scope = useScopeContext();
  const [allConnections, setAllConnections] = useState<Connection[]>([]);
  const [enabledConnectionIds, setEnabledConnectionIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, [agentId, scope.teamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [connections, agentConnections] = await Promise.all([
        connectionManagementService.getAllConnections(scope.teamId),
        agentsService.getAgentConnections(scope.teamId, agentId),
      ]);
      setAllConnections(connections);
      const enabledIds = new Set(
        agentConnections.map((c: Connection) => c.connectionId || c.id)
      );
      setEnabledConnectionIds(enabledIds);
    } catch (err) {
      console.error('Failed to load connections:', err);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (connection: Connection, enable: boolean) => {
    const connId = connection.connectionId || connection.id;
    setTogglingIds(prev => new Set(prev).add(connId));

    try {
      // Fetch current permissions to preserve toolPermissions
      const permissions = await connectionManagementService.getPermissions(scope.teamId, connId);
      const currentAgentIds = permissions.agentIds || [];

      let newAgentIds: string[];
      if (enable) {
        newAgentIds = currentAgentIds.includes(agentId)
          ? currentAgentIds
          : [...currentAgentIds, agentId];
      } else {
        newAgentIds = currentAgentIds.filter(id => id !== agentId);
      }

      await connectionManagementService.updatePermissions(scope.teamId, connId, {
        agentIds: newAgentIds,
        toolPermissions: permissions.toolPermissions,
      });

      // Update local state
      setEnabledConnectionIds(prev => {
        const next = new Set(prev);
        if (enable) {
          next.add(connId);
        } else {
          next.delete(connId);
        }
        return next;
      });

      toast.success(`${connection.name} ${enable ? 'enabled' : 'disabled'} for this agent`);
    } catch (err) {
      console.error('Failed to update connection:', err);
      toast.error(`Failed to update ${connection.name}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(connId);
        return next;
      });
    }
  };

  const enabledCount = enabledConnectionIds.size;

  if (loading) {
    return (
      <div class="flex justify-center p-8">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold">Agent Connections</h2>
          <p class="text-sm opacity-70">
            Toggle connections on or off for this agent. {enabledCount} of {allConnections.length} enabled.
          </p>
        </div>
      </div>

      {enabledCount > 5 && (
        <div class="alert alert-warning">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 class="font-bold">Too many connections</h3>
            <div class="text-sm">
              This agent has {enabledCount} connections enabled. More than 5 can degrade AI performance — the model may struggle with tool selection when too many tools are available.
            </div>
          </div>
        </div>
      )}

      {allConnections.length === 0 ? (
        <div class="alert alert-info">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span>No connections have been set up for this team yet.</span>
        </div>
      ) : (
        <div class="space-y-3">
          {allConnections.map((conn) => {
            const connId = conn.connectionId || conn.id;
            const isEnabled = enabledConnectionIds.has(connId);
            const isToggling = togglingIds.has(connId);

            return (
              <div key={connId} class="card bg-base-200">
                <div class="card-body p-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="min-w-0">
                        <div class="flex items-center gap-2">
                          <span class="font-medium truncate">{conn.name}</span>
                          <span class={`badge badge-sm ${conn.active ? 'badge-success' : 'badge-ghost'}`}>
                            {conn.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        {conn.connector?.name && (
                          <div class="text-xs opacity-60">{conn.connector.name}</div>
                        )}
                      </div>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                      {isToggling && (
                        <span class="loading loading-spinner loading-xs"></span>
                      )}
                      <input
                        type="checkbox"
                        class="toggle toggle-primary"
                        checked={isEnabled}
                        disabled={isToggling}
                        onChange={() => handleToggle(conn, !isEnabled)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
